import {
  Stack,
  Tooltip,
  Box,
  Flex,
  useDisclosure,
  IconButton,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  memo,
  useMemo,
  useRef,
  type TransitionEvent,
} from "react";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import { getGaraazStateColor, OrderActions, OrderStatus } from "../enums";
import { AllocatedTo, startOrcaStates } from "./GaraazOrderComponent";
import { removeUnderScoreWithUpperCase } from "../../../utils/removeUnderscore";
import GaraazOrderComponentHeader from "./GaraazOrderComponentHeader";
import GaraazOrderComponentBody from "./GaraazOrderComponentBody";
import GaraazOrderComponentFooter from "./GaraazOrderComponentFooter";
import GaraazOrderComponentFooterVirtual from "./GaraazOrderComponentFooterVirtual";
import GaraazOrderComponentFooterReturn from "./GaraazOrderComponentFooterReturn";
import GaraazOrderComponentFooterSubscription from "./GaraazOrderComponentFooterSubscription";
import {
  OrderLabelProvider,
  GetOrderLabels,
} from "./GaraazOrderCard/OrderLabelContext";
import { hasGaraazInvoiceUploaded, isTestGaraazOrder } from "../orderUtils";
import TestOrderBadge, { TEST_ORDER_COLOR } from "./GaraazOrderCard/TestOrderBadge";
import GaraazOrderVehicleInfoStrip, {
  hasVehicleInfoStripData,
} from "./GaraazOrderVehicleInfoStrip";

type GaraazOrderCardProps = {
  orderData: any;
  orderHandlers: any;
  orderCancellationReason: string[];
  refetchAllOrders: () => void;
  isChildOrder?: boolean;
  onToggleChildOrders: (orderId: string) => void;
  reFetchChildOrders: () => void;
  isChildOrdersVisible: boolean;
  findBrandName: (brandId: string) => string;
  getSupplierBusinessName: (supplierId: string) => string;
  getSupplierParentId: (supplierId: string) => string;
  getSupplierBranchName: (supplierId: string, branchId: string) => string;
  getSupplierNumber: (supplierId: string, branchId?: string) => string;
  isCustomerOrderFreezed?: boolean;
  isPartsFreezed?: boolean;
};

const EXPANDED_CARD_HEIGHT = "65vh";
const STATUS_BAR_HEIGHT_PX = 6;
const CARD_HEIGHT_TRANSITION =
  "height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), border-color 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)";

const getInitialBodyVisible = (
  orderData: any,
  isChildOrder: boolean,
): boolean => {
  if (isChildOrder) return false;
  if (
    orderData.orderType === "SUBSCRIPTION" ||
    startOrcaStates.includes(orderData.orderStatus) ||
    orderData.orderStatus === OrderStatus.READY_TO_DISPATCH ||
    orderData.orderStatus === OrderStatus.DELIVERED
  ) {
    return false;
  }
  return true;
};

const GaraazOrderCard: React.FC<GaraazOrderCardProps> = memo(
  ({
    orderData,
    orderCancellationReason,
    refetchAllOrders,
    isChildOrder = false,
    onToggleChildOrders,
    reFetchChildOrders,
    isChildOrdersVisible,
    findBrandName,
    getSupplierBusinessName,
    getSupplierParentId,
    getSupplierBranchName,
    getSupplierNumber,
    isCustomerOrderFreezed = false,
    isPartsFreezed = false,
  }) => {
    const [isBodyVisible, setIsBodyVisible] = useState(() =>
      getInitialBodyVisible(orderData, isChildOrder),
    );
    // Animatable height: px ↔ 65vh (CSS cannot transition to/from `auto`).
    const [cardHeight, setCardHeight] = useState<string>(() =>
      getInitialBodyVisible(orderData, isChildOrder)
        ? EXPANDED_CARD_HEIGHT
        : "auto",
    );

    const cardRef = useRef<HTMLDivElement | null>(null);
    const headerWrapRef = useRef<HTMLDivElement | null>(null);
    const vehicleWrapRef = useRef<HTMLDivElement | null>(null);
    const collapseSeparatorRef = useRef<HTMLDivElement | null>(null);
    const footerWrapRef = useRef<HTMLDivElement | null>(null);

    const [orderStatus, setOrderStatus] = useState(
      orderData?.orderStatus ?? "",
    );
    const [transactionType, setTransactionType] = useState(
      orderData?.transactionType,
    );
    const [displayPartConversion, setDisplayPartConversion] = useState("flex");
    const [isEstimateRequired, setIsEstimateRequired] = useState(
      orderData?.requireEstimate,
    );
    const [garaazRemarks, setGaraazRemarks] = useState(orderData.garaazRemarks);
    const [supplierCancellationReasons, setSupplierCancellationReasons] =
      useState(orderData.supplierCancellationReasons);
    const [customerHoldReasons, setCustomerHoldReasons] = useState(
      orderData.customerHoldReasons,
    );
    const [promiseTimeBreachReasons, setPromiseTimeBreachReasons] = useState(
      orderData.promiseTimeBreachReasons,
    );
    const [etaMissed, setEtaMissed] = useState(false);

    const {
      isOpen: etaPickerModalOpen,
      onOpen: openETAPickerModal,
      onClose: closeETAPickerModal,
    } = useDisclosure();
    const prefersReducedMotion = usePrefersReducedMotion();

    const activesupplierOrderStatus: AllocatedTo = orderData?.allocatedTo?.find(
      (item: AllocatedTo) => item.isActive,
    );

    useEffect(() => {
      setOrderStatus(orderData?.orderStatus || "");
      setTransactionType(orderData?.transactionType);
      setEtaMissed(false);
    }, [orderData?.orderStatus, orderData?.transactionType]);

    const displayOrderStatus = useMemo(() => {
      if (
        orderStatus === OrderStatus.READY_TO_DISPATCH &&
        orderData?.orderStatus !== OrderStatus.READY_TO_DISPATCH
      ) {
        return orderData?.orderStatus ?? orderStatus;
      }
      return orderStatus;
    }, [orderStatus, orderData?.orderStatus]);

    const measureCollapsedHeightPx = useCallback(() => {
      const headerH = headerWrapRef.current?.offsetHeight ?? 0;
      const vehicleH = vehicleWrapRef.current?.offsetHeight ?? 0;
      const separatorH = collapseSeparatorRef.current?.offsetHeight ?? 0;
      const footerH = footerWrapRef.current?.offsetHeight ?? 0;
      // Status bar + chrome sections + 1px top/bottom card borders.
      return (
        STATUS_BAR_HEIGHT_PX + headerH + vehicleH + separatorH + footerH + 2
      );
    }, []);

    useLayoutEffect(() => {
      const effectiveStatus = displayOrderStatus;
      let nextVisible: boolean;
      if (isChildOrder) {
        nextVisible = false;
      } else if (
        startOrcaStates.includes(effectiveStatus) ||
        effectiveStatus === OrderStatus.READY_TO_DISPATCH ||
        effectiveStatus === OrderStatus.DELIVERED ||
        orderData.orderType === "SUBSCRIPTION"
      ) {
        nextVisible = false;
      } else {
        nextVisible = true;
      }
      setIsBodyVisible(nextVisible);
      setCardHeight(nextVisible ? EXPANDED_CARD_HEIGHT : "auto");
    }, [displayOrderStatus, orderData.orderType, isChildOrder]);

    const handlePartConversionChangeScreen = useCallback(() => {
      setDisplayPartConversion((prev) => (prev === "flex" ? "none" : "flex"));
    }, []);

    const expandBody = useCallback(() => {
      // Reduced motion: skip delayed height animation; still settle collapsed to auto.
      if (prefersReducedMotion) {
        if (isBodyVisible) {
          setIsBodyVisible(false);
          setCardHeight("auto");
        } else {
          setIsBodyVisible(true);
          setCardHeight(EXPANDED_CARD_HEIGHT);
        }
        return;
      }

      const el = cardRef.current;
      if (!el) {
        setIsBodyVisible((prev) => !prev);
        setCardHeight((prev) =>
          prev === EXPANDED_CARD_HEIGHT ? "auto" : EXPANDED_CARD_HEIGHT,
        );
        return;
      }

      if (isBodyVisible) {
        // Collapse: lock current height, then animate down to chrome size.
        const fromPx = el.offsetHeight;
        const toPx = measureCollapsedHeightPx();
        setCardHeight(`${fromPx}px`);
        // Double rAF so the browser paints `from` before transitioning to `to`.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsBodyVisible(false);
            setCardHeight(`${toPx}px`);
          });
        });
      } else {
        // Expand: lock current height, then animate up to 65vh.
        const fromPx = el.offsetHeight;
        setCardHeight(`${fromPx}px`);
        setIsBodyVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setCardHeight(EXPANDED_CARD_HEIGHT);
          });
        });
      }
    }, [isBodyVisible, measureCollapsedHeightPx, prefersReducedMotion]);

    const handleCardHeightTransitionEnd = useCallback(
      (event: TransitionEvent<HTMLDivElement>) => {
        if (event.propertyName !== "height") return;
        if (event.target !== event.currentTarget) return;
        // After collapse settles, allow natural sizing if LPN/footer content changes.
        if (!isBodyVisible) {
          setCardHeight("auto");
        }
      },
      [isBodyVisible],
    );

    const isEnquiryOrder = useMemo(() => {
      return (
        orderData?.requireEstimate === true &&
        orderData?.rootOrderId === orderData?._id
      );
    }, [orderData?.requireEstimate, orderData?.rootOrderId, orderData?._id]);

    const isEnquiryLocked = useMemo(() => {
      return isEnquiryOrder && hasGaraazInvoiceUploaded(orderData);
    }, [isEnquiryOrder, orderData?.invoiceInfo]);

    const orderLabel = useMemo(() => {
      return isEnquiryOrder ? GetOrderLabels.ENQUIRY : GetOrderLabels.ORDER;
    }, [isEnquiryOrder]);

    const orderStatusLabel = useMemo(
      () => removeUnderScoreWithUpperCase(displayOrderStatus ?? ""),
      [displayOrderStatus],
    );

    const isTestOrder = isTestGaraazOrder(orderData);
    const hasVehicleInfo = hasVehicleInfoStripData(orderData);
    const canToggleBody = !startOrcaStates.includes(orderStatus);
    const testOrderBorderWidth = 5;
    const testOrderInsetShadow = `inset ${testOrderBorderWidth}px 0 0 0 ${TEST_ORDER_COLOR}, inset -${testOrderBorderWidth}px 0 0 0 ${TEST_ORDER_COLOR}, inset 0 -${testOrderBorderWidth}px 0 0 ${TEST_ORDER_COLOR}`;
    // Collapsed cards size to content so header + vehicle strip + footer
    // never overlap, and ACK stays a normal-height button (not stretched).

    const collapseToggleButton = canToggleBody ? (
      <IconButton
        width="1.2em"
        height="1.2em"
        lineHeight={1}
        minWidth="none"
        aria-label={
          isBodyVisible ? "Collapse order body" : "Expand order body"
        }
        borderRadius="50%"
        onClick={expandBody}
        icon={isBodyVisible ? <BiChevronUp /> : <BiChevronDown />}
      />
    ) : null;

    return (
      <OrderLabelProvider value={{ orderLabel, isEnquiryOrder }}>
        <Stack
          ref={cardRef}
          id={orderData._id}
          position="relative"
          width={isChildOrder ? "calc(75vw - 55px)" : "75vw"}
          minWidth={"360px"}
          maxWidth={"1400px"}
          // Expanded: fixed 65vh. Collapsed: measured chrome px (animatable), then auto.
          // Keep overflow visible so Test badge / header text are not clipped.
          height={cardHeight}
          overflow="visible"
          border="1px solid"
          borderColor={isTestOrder ? "orange.200" : "lightgrey"}
          bg={isTestOrder ? "orange.50" : "white"}
          borderRadius={"8px"}
          marginTop={"40px !important"}
          display={"flex"}
          flexDirection={"column"}
          className={isChildOrder ? "child-order" : "order"}
          gap={0}
          ml={isChildOrder ? 4 : 0}
          boxShadow={
            isTestOrder
              ? `${testOrderInsetShadow}, 0 2px 12px rgba(221, 107, 32, 0.12)`
              : undefined
          }
          transition={prefersReducedMotion ? undefined : CARD_HEIGHT_TRANSITION}
          onTransitionEnd={
            prefersReducedMotion ? undefined : handleCardHeightTransitionEnd
          }
          _hover={{
            ...(isTestOrder
              ? {
                  boxShadow: `${testOrderInsetShadow}, 0 8px 28px rgba(221, 107, 32, 0.22)`,
                  borderColor: "orange.300",
                }
              : {
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
                  borderColor: "teal.200",
                }),
          }}
          _focus={{
            boxShadow: "0 0 0 3px rgba(45, 212, 191, 0.2)",
          }}
        >
          {isTestOrder && <TestOrderBadge />}
          <Flex
            direction={"column"}
            gap={0}
            height={"100%"}
            minH={0}
            flex={1}
            borderRadius={"8px"}
            overflow={isBodyVisible ? "hidden" : "visible"}
          >
            <Box height={"6px"} position="relative" width="100%" flexShrink={0}>
              <Tooltip
                label={orderStatusLabel}
                placement="left-start"
                hasArrow
                cursor={"pointer"}
                color={"white"}
                bg={getGaraazStateColor(displayOrderStatus)}
                position={"relative"}
              >
                <div
                  style={{
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    background: `linear-gradient(white, white) padding-box, ${getGaraazStateColor(
                      displayOrderStatus,
                    )} border-box`,
                    borderTop: "6px solid transparent",
                    height: "6px",
                    width: "100%",
                    top: 0,
                    left: 0,
                    overflow: "hidden",
                  }}
                />
              </Tooltip>
            </Box>
            <Box height={"100%"} display="flex" flexDirection="column" minH={0}>
              <Flex direction={"column"} gap={0} height={"100%"} minH={0}>
                <Box
                  ref={headerWrapRef}
                  position="relative"
                  flexShrink={0}
                  w="100%"
                >
                  <GaraazOrderComponentHeader
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    etaMissed={etaMissed}
                    setEtaMissed={setEtaMissed}
                    findBrandName={findBrandName}
                    getSupplierBusinessName={getSupplierBusinessName}
                    getSupplierParentId={getSupplierParentId}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    transactionType={transactionType}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    getSupplierBranchName={getSupplierBranchName}
                    getSupplierNumber={getSupplierNumber}
                    isEstimateRequired={isEstimateRequired}
                    isUrgent={!!orderData?.isUrgent}
                    partType={orderData?.partType}
                    refetchAllOrders={refetchAllOrders}
                    onOpenCptUpdate={openETAPickerModal}
                  />
                  {/* No LPN bar: keep collapse on header edge (UAT) — avoids orange gap strip. */}
                  {!hasVehicleInfo && collapseToggleButton && (
                    <Box
                      position="absolute"
                      left="50%"
                      bottom={-2}
                      transform="translateX(-50%)"
                      zIndex={2}
                      bg="white"
                      borderRadius="full"
                      lineHeight={0}
                    >
                      {collapseToggleButton}
                    </Box>
                  )}
                </Box>

                {hasVehicleInfo && (
                  <Box
                    ref={vehicleWrapRef}
                    position="relative"
                    flexShrink={0}
                    w="100%"
                    bg="white"
                    pb={collapseToggleButton ? 2 : 0}
                  >
                    <GaraazOrderVehicleInfoStrip orderData={orderData} />
                  </Box>
                )}

                {/* With LPN bar only: separator + chevron between vehicle strip and body/footer. */}
                {hasVehicleInfo && collapseToggleButton && (
                  <Box
                    ref={collapseSeparatorRef}
                    flexShrink={0}
                    w="100%"
                    h="1px"
                    bg="blackAlpha.200"
                    position="relative"
                    zIndex={2}
                  >
                    <Flex
                      position="absolute"
                      left="50%"
                      top="50%"
                      transform="translate(-50%, -50%)"
                      align="center"
                      justify="center"
                      bg="white"
                      px={1}
                      lineHeight={0}
                      borderRadius="full"
                    >
                      {collapseToggleButton}
                    </Flex>
                  </Box>
                )}

                {!startOrcaStates.includes(orderStatus) && isBodyVisible && (
                  <GaraazOrderComponentBody
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    displayPartConversion={displayPartConversion}
                    findBrandName={findBrandName}
                    getSupplierBusinessName={getSupplierBusinessName}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    garaazRemarks={garaazRemarks}
                    isEnquiryLocked={isEnquiryLocked}
                    isPartsFreezed={isPartsFreezed}
                    refetchAllOrders={refetchAllOrders}
                  />
                )}

                <Box
                  ref={footerWrapRef}
                  flexShrink={0}
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                >
                {orderData.orderType === "REGULAR" ? (
                  <GaraazOrderComponentFooter
                    setOrderStatus={setOrderStatus}
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    setTransactionType={setTransactionType}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                ) : orderData.orderType === "VIRTUAL" ? (
                  <GaraazOrderComponentFooterVirtual
                    orderData={orderData}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    onToggleChildOrders={onToggleChildOrders}
                    refetchAllOrders={refetchAllOrders}
                    reFetchChildOrders={reFetchChildOrders}
                    setTransactionType={setTransactionType}
                    isChildOrder={isChildOrder}
                    orderStatus={orderStatus}
                    setOrderStatus={setOrderStatus}
                  />
                ) : orderData.orderType === "RETURN" ? (
                  <GaraazOrderComponentFooterReturn
                    setOrderStatus={setOrderStatus}
                    activesupplierOrderStatus={activesupplierOrderStatus}
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    garaazRemarks={garaazRemarks}
                    setGaraazRemarks={setGaraazRemarks}
                    supplierCancellationReasons={supplierCancellationReasons}
                    setSupplierCancellationReasons={
                      setSupplierCancellationReasons
                    }
                    customerHoldReasons={customerHoldReasons}
                    setCustomerHoldReasons={setCustomerHoldReasons}
                    promiseTimeBreachReasons={promiseTimeBreachReasons}
                    setPromiseTimeBreachReasons={setPromiseTimeBreachReasons}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                ) : (
                  <GaraazOrderComponentFooterSubscription
                    handlePartConversionChangeScreen={
                      handlePartConversionChangeScreen
                    }
                    orderCancellationReason={orderCancellationReason}
                    orderData={orderData}
                    orderStatus={orderStatus}
                    refetchAllOrders={refetchAllOrders}
                    setEtaMissed={setEtaMissed}
                    setOrderStatus={setOrderStatus}
                    isCustomerOrderFreezed={isCustomerOrderFreezed}
                    isEnquiryLocked={isEnquiryLocked}
                    isChildOrder={isChildOrder}
                    isChildOrdersVisible={isChildOrdersVisible}
                    etaPickerModalOpen={etaPickerModalOpen}
                    closeETAPickerModal={closeETAPickerModal}
                  />
                )}
                </Box>
              </Flex>
            </Box>
          </Flex>
        </Stack>
      </OrderLabelProvider>
    );
  },
);

GaraazOrderCard.displayName = "GaraazOrderCard";

export default GaraazOrderCard;
